/**
 * App-wide providers — Query client + Toaster.
 *
 * Auth is provided via the `useAuth` hook directly (no context) since the
 * underlying state lives in module-scope (`authStore`) and TanStack Query.
 * Theme provider lands in Phase 3 with the admin shell.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Toaster } from 'sonner';

import { ConfirmProvider } from '../components/ConfirmProvider';
import { ErrorBoundary } from '../components/ErrorBoundary';

export function Providers({ children }: { children: ReactNode }): JSX.Element {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: 0,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <ConfirmProvider>{children}</ConfirmProvider>
      </ErrorBoundary>
      <Toaster
        position="bottom-right"
        toastOptions={{
          classNames: {
            toast: 'rounded border border-rule bg-paper text-ink shadow-sm',
            description: 'text-ink-muted',
          },
        }}
      />
    </QueryClientProvider>
  );
}
